import { updateProfile } from 'firebase/auth';
import { useAuthStore } from '../stores/auth';

/**
 * Syncs user profile data from database to Firebase Auth
 * Use this after checkout to update displayName if missing
 */
export function useProfileSync() {
  const authStore = useAuthStore();

  /**
   * Updates Firebase Auth displayName if it's not set
   * Fetches the name from our database (populated via Stripe session)
   */
  async function syncDisplayName(): Promise<void> {
    const user = authStore.user;

    // Only sync if user is logged in and displayName is not set
    if (!user || user.displayName) {
      return;
    }

    try {
      // Get auth token
      const token = await authStore.getIdToken();
      if (!token) {
        console.warn('Cannot sync displayName: no auth token');
        return;
      }

      // Fetch user data from our database
      const response = await fetch(`/api/users/${user.uid}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error('Failed to fetch user data for displayName sync:', response.statusText);
        return;
      }

      const userData = await response.json();

      // If we have a name in the database, update Firebase Auth
      if (userData.name) {
        await updateProfile(user, {
          displayName: userData.name,
        });

        console.log('Firebase Auth displayName synced from database:', userData.name);

        // Force refresh the auth state to update the displayName in the UI
        await user.reload();
      }
    } catch (error) {
      console.error('Error syncing displayName:', error);
    }
  }

  return {
    syncDisplayName,
  };
}
